import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ApiResponse,
  OrdersResponse,
  Order,
  ActiveProtocol,
  ProtocolsResponse,
  BillingProfile,
  Invoice,
  Notification,
  ChatThread,
  ChatMessage,
  Call,
  BillingCardsResponse,
  BillingPaymentMethod
} from '../models/patientportal';
import { baseURL } from '../constants/base.url';


@Injectable({
  providedIn: 'root'
})
export class PatientPortalService {

  constructor(private http: HttpClient) { }

  // =========================
  // PARAM BUILDER
  // =========================
  private buildParams(params?: any): HttpParams {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value);
        }
      });
    }

    return httpParams;
  }

  // =========================
  // PROFILE
  // =========================
  getMyProfile(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${baseURL}patient-portal/my-profile`
    );
  }

  // =========================
  // PROTOCOLS
  // =========================
  getActiveProtocol(): Observable<ApiResponse<{ protocol: ActiveProtocol | null }>> {
    return this.http.get<ApiResponse<{ protocol: ActiveProtocol | null }>>(
      `${baseURL}patient-portal/protocols/active`
    );
  }

  getProtocols(params?: any): Observable<ApiResponse<ProtocolsResponse>> {
    return this.http.get<ApiResponse<ProtocolsResponse>>(
      `${baseURL}patient-portal/protocols/list`,
      { params: this.buildParams(params) }
    );
  }

  // =========================
  // ORDERS
  // =========================
  getMyOrders(params?: any): Observable<ApiResponse<OrdersResponse>> {
    return this.http.get<ApiResponse<OrdersResponse>>(
      `${baseURL}patient-portal/orders`,
      { params: this.buildParams(params) }
    );
  }

  getMyOrderById(id: string): Observable<ApiResponse<{ order: Order }>> {
    return this.http.get<ApiResponse<{ order: Order }>>(
      `${baseURL}patient-portal/orders/${id}`
    );
  }

  getStepIndex(status: string): number {
    const steps = ['processing', 'dispatched', 'delivered'];
    return steps.indexOf(status?.toLowerCase());
  }

  trackOrder(trackingNo: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${baseURL}orders/track/${trackingNo}`
    );
  }

  // =========================
  // BILLING
  // =========================
  getBillingProfile(): Observable<ApiResponse<{ profile: BillingProfile }>> {
    return this.http.get<ApiResponse<{ profile: BillingProfile }>>(
      `${baseURL}patient-portal/billing/profile`
    );
  }

  // =========================
  // BILLING CARDS
  // =========================

  getMyCards(): Observable<ApiResponse<BillingCardsResponse>> {
    return this.http.get<ApiResponse<BillingCardsResponse>>(
      `${baseURL}patient-portal/billing/cards`
    );
  }

  attachCard(
    data: { paymentMethodId: string; setAsDefault?: boolean }
  ): Observable<ApiResponse<{ paymentMethod: BillingPaymentMethod }>> {
    return this.http.post<ApiResponse<{ paymentMethod: BillingPaymentMethod }>>(
      `${baseURL}patient-portal/billing/cards`,
      data
    );
  }


  setDefaultCard(paymentMethodId: string): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(
      `${baseURL}patient-portal/billing/cards/default`,
      { paymentMethodId }
    );
  }

  removeCard(paymentMethodId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${baseURL}patient-portal/billing/cards/${paymentMethodId}`
    );
  }

  getInvoices(params?: any): Observable<ApiResponse<{ invoices: Invoice[] }>> {
    return this.http.get<ApiResponse<{ invoices: Invoice[] }>>(
      `${baseURL}patient-portal/invoices`,
      { params: this.buildParams(params) }
    );
  }

  getInvoiceById(id: string): Observable<ApiResponse<{ invoice: Invoice }>> {
    return this.http.get<ApiResponse<{ invoice: Invoice }>>(
      `${baseURL}patient-portal/invoices/${id}`
    );
  }

  // =========================
  // NOTIFICATIONS
  // =========================
  getNotifications(params?: any): Observable<ApiResponse<{ notifications: Notification[] }>> {
    return this.http.get<ApiResponse<{ notifications: Notification[] }>>(
      `${baseURL}patient-portal/notifications`,
      { params: this.buildParams(params) }
    );
  }

  markNotificationsRead(ids: string[]): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${baseURL}patient-portal/notifications/read`,
      { notificationIds: ids }
    );
  }

  // =========================
  // CHAT
  // =========================
  getThreads(params?: any): Observable<ApiResponse<{ threads: ChatThread[] }>> {
    return this.http.get<ApiResponse<{ threads: ChatThread[] }>>(
      `${baseURL}patient-portal/chat/threads`,
      { params: this.buildParams(params) }
    );
  }

  getMessages(threadId: string, params?: any): Observable<ApiResponse<{ messages: ChatMessage[] }>> {
    return this.http.get<ApiResponse<{ messages: ChatMessage[] }>>(
      `${baseURL}patient-portal/chat/threads/${threadId}/messages`,
      { params: this.buildParams(params) }
    );
  }

  sendMessage(data: { threadId: string; message: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${baseURL}patient-portal/chat/messages`,
      data
    );
  }

  // =========================
  // CALLS
  // =========================
  getCalls(params?: any): Observable<ApiResponse<{ calls: Call[] }>> {
    return this.http.get<ApiResponse<{ calls: Call[] }>>(
      `${baseURL}patient-portal/calls`,
      { params: this.buildParams(params) }
    );
  }
}